import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";
import { useStarterIngredients } from "@/hooks/useStarterIngredients";
import { useStarterCategories } from "@/hooks/useStarterCategories";
import { showError } from "@/lib/toastHelpers";

const UNITS = ["g", "kg", "ml", "l", "pcs", "pack", "box", "tsp", "tbsp", "cup", "oz", "lb"];

export default function AddStarterIngredient() {
  const [location, setLocation] = useLocation();
  const [editId, setEditId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    categoryId: "",
    unit: "g",
    quantity: "",
    purchaseAmount: "",
    isCountBased: false,
    purchaseUnit: "",
    piecesPerPurchaseUnit: "",
    weightPerPiece: "",
    notes: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('edit');
    setEditId(id);
  }, [location]);

  const isEditMode = !!editId;

  const { starterIngredients, ingredientsLoading, createIngredientMutation, updateIngredientMutation } = useStarterIngredients({
    onCreateSuccess: () => setLocation("/starter-pack"),
    onUpdateSuccess: () => setLocation("/starter-pack"),
  });

  const { categories: starterIngredientCategories, isLoading: categoriesLoading } = useStarterCategories('ingredients');

  const editingIngredient = isEditMode && Array.isArray(starterIngredients)
    ? starterIngredients.find((i: any) => i._id === editId)
    : undefined;

  useEffect(() => {
    if (editingIngredient) {
      setFormData({
        name: editingIngredient.name || "",
        categoryId: editingIngredient.categoryId || "",
        unit: editingIngredient.unit || "g",
        quantity: editingIngredient.quantity || "",
        purchaseAmount: editingIngredient.purchaseAmount || "",
        isCountBased: editingIngredient.isCountBased || false,
        purchaseUnit: editingIngredient.purchaseUnit || "",
        piecesPerPurchaseUnit: editingIngredient.piecesPerPurchaseUnit || "",
        weightPerPiece: editingIngredient.weightPerPiece || "",
        notes: editingIngredient.notes || "",
      });
    }
  }, [editingIngredient]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError("Name is required");
      return;
    }

    // pricePerGram is intentionally NOT computed here — this app has no unit-conversion
    // utility, so a naive purchaseAmount/quantity division ignored `unit` entirely (e.g.
    // "1 kg for ₱200" would have stored 200 instead of 0.2, a 1000x overstatement). The
    // backend derives it through PantryPricingService, the same single source of truth
    // every real ingredient create/update route already uses. Bug #81.
    const data = {
      name: formData.name.trim(),
      categoryId: formData.categoryId || null,
      unit: formData.unit || null,
      quantity: formData.quantity || null,
      purchaseAmount: formData.purchaseAmount || null,
      isCountBased: formData.isCountBased,
      purchaseUnit: formData.isCountBased ? formData.purchaseUnit || null : null,
      piecesPerPurchaseUnit: formData.isCountBased ? formData.piecesPerPurchaseUnit || null : null,
      weightPerPiece: formData.isCountBased ? formData.weightPerPiece || null : null,
      notes: formData.notes || null,
    };

    if (isEditMode && editId) {
      updateIngredientMutation.mutate({ id: editId, ...data });
    } else {
      createIngredientMutation.mutate(data);
    }
  };

  if (ingredientsLoading || categoriesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isEditMode && !editingIngredient) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/starter-pack")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
            {isEditMode ? 'Edit Starter Ingredient' : 'Add Starter Ingredient'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update starter ingredient details' : 'Create a new starter ingredient template'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ingredient Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ingredient name"
                required
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(starterIngredientCategories || []).map((cat: any) => (
                    <SelectItem key={cat._id} value={cat._id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.001"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="purchaseAmount">Purchase Amount</Label>
              <Input
                id="purchaseAmount"
                type="number"
                step="0.01"
                value={formData.purchaseAmount}
                onChange={(e) => setFormData({ ...formData, purchaseAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isCountBased"
                checked={formData.isCountBased}
                onCheckedChange={(checked) => setFormData({ ...formData, isCountBased: !!checked })}
              />
              <Label htmlFor="isCountBased">Count-based ingredient</Label>
            </div>

            {formData.isCountBased && (
              <div className="space-y-4 pl-6 border-l-2 border-muted">
                <div>
                  <Label htmlFor="purchaseUnit">Purchase Unit</Label>
                  <Input
                    id="purchaseUnit"
                    value={formData.purchaseUnit}
                    onChange={(e) => setFormData({ ...formData, purchaseUnit: e.target.value })}
                    placeholder="e.g. tray, dozen"
                  />
                </div>
                <div>
                  <Label htmlFor="piecesPerPurchaseUnit">Pieces per Purchase Unit</Label>
                  <Input
                    id="piecesPerPurchaseUnit"
                    type="number"
                    value={formData.piecesPerPurchaseUnit}
                    onChange={(e) => setFormData({ ...formData, piecesPerPurchaseUnit: e.target.value })}
                    placeholder="e.g. 12"
                  />
                </div>
                <div>
                  <Label htmlFor="weightPerPiece">Weight per Piece (g)</Label>
                  <Input
                    id="weightPerPiece"
                    type="number"
                    step="0.1"
                    value={formData.weightPerPiece}
                    onChange={(e) => setFormData({ ...formData, weightPerPiece: e.target.value })}
                    placeholder="e.g. 55"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLocation("/starter-pack")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createIngredientMutation.isPending || updateIngredientMutation.isPending}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
